import React, { useState } from "react";
import { Form, Alert } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import {
  FaUserPlus,
  FaUser,
  FaEnvelope,
  FaLock,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

import AuthLayout from "../components/AuthLayout";
import {
  AuthField,
  PasswordField,
  PasswordStrength,
} from "../components/AuthFields";
import { useAuth } from "../context/AuthContext";

/* Signing up is an arrival, not a return, so the panel sells what an account
   actually gets you rather than repeating the consultancy pitch on /login. */
const POINTS = [
  "Capture ideas on the shared sticky-notes board",
  "Arrange them into layouts in the design studio",
  "Keep your account details in one place",
];

function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await register(form.name, form.email, form.password);
      // New accounts are normal users; land them on their account page
      navigate("/account", { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Signup failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Only worth showing once there is something to compare against
  const showMatch = form.confirm.length > 0;
  const matches = form.password === form.confirm;

  return (
    <AuthLayout
      icon={<FaUserPlus />}
      eyebrow="Get started"
      title="Create Account"
      subtitle="Sign up to get started with Sarvavya"
      brandTitle="Start building with Sarvavya."
      brandLead="Create an account to use the board and the design studio."
      points={POINTS}
      footer={
        <>
          Already have an account? <Link to="/login">Log in</Link>
        </>
      }
    >
      {/* aria-live so validation failures are announced, not only shown */}
      <div aria-live="polite">
        {error && <Alert variant="danger">{error}</Alert>}
      </div>

      <Form onSubmit={handleSubmit}>
        <AuthField
          label="Full Name"
          icon={<FaUser />}
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Jane Doe"
          autoComplete="name"
          required
        />

        <AuthField
          label="Email"
          icon={<FaEnvelope />}
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="you@example.com"
          autoComplete="username"
          required
        />

        <PasswordField
          label="Password"
          icon={<FaLock />}
          name="password"
          value={form.password}
          onChange={handleChange}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          required
        >
          <PasswordStrength value={form.password} />
        </PasswordField>

        <PasswordField
          className="mb-4"
          label="Confirm Password"
          icon={<FaLock />}
          name="confirm"
          value={form.confirm}
          onChange={handleChange}
          placeholder="Re-enter your password"
          autoComplete="new-password"
          required
        >
          {/* Catches a typo while it is still cheap to fix, rather than on submit */}
          <div aria-live="polite">
            {showMatch && (
              <p className={`auth-match ${matches ? "is-ok" : "is-bad"}`}>
                {matches ? (
                  <>
                    <FaCheckCircle aria-hidden="true" /> Passwords match
                  </>
                ) : (
                  <>
                    <FaTimesCircle aria-hidden="true" /> Passwords do not match
                  </>
                )}
              </p>
            )}
          </div>
        </PasswordField>

        <button type="submit" className="auth-submit" disabled={submitting}>
          {submitting ? "Creating account…" : "Sign Up"}
        </button>
      </Form>
    </AuthLayout>
  );
}

export default Signup;
